import clsx from "clsx";
import { useState } from "react";
import { useFetcher } from "@remix-run/react";
import {
  getBusinessType,
  getListingStatus,
} from "~/utils/const";

// RemixのJSONシリアライゼーション対応型
type SerializedUserProfile = {
  business_type?: string | null;
  postal_code?: string | null;
  address?: string | null;
  phone_number?: string | null;
  representative_name?: string | null;
  listing_status?: string | null;
  capital?: number | null;
  established_at?: string | null;
};

interface CompanyInfoProps {
  userProfile: SerializedUserProfile;
  isEditable?: boolean;
  userId?: string;
  onEdit?: (
    profile: SerializedUserProfile,
  ) => void;
}

export default function CompanyInfo({
  userProfile,
  isEditable = false,
  userId,
  onEdit,
}: CompanyInfoProps) {
  const [isEditMode, setIsEditMode] =
    useState(false);
  const [editingProfile, setEditingProfile] =
    useState<SerializedUserProfile>(userProfile);
  const fetcher = useFetcher();

  const handleUpdateProfile = (
    field: keyof SerializedUserProfile,
    value: any,
  ) => {
    setEditingProfile({
      ...editingProfile,
      [field]: value,
    });
  };

  const handleSave = () => {
    const formData = new FormData();
    formData.append(
      "profile",
      JSON.stringify(editingProfile),
    );

    fetcher.submit(formData, {
      method: "post",
      action: "/api/company-info/update",
    });

    onEdit?.(editingProfile);
    setIsEditMode(false);
  };

  const handleCancel = () => {
    setEditingProfile(userProfile);
    setIsEditMode(false);
  };

  const displayProfile = isEditMode
    ? editingProfile
    : userProfile;

  const compData = [
    {
      data: displayProfile.business_type,
      text: "業種",
      field:
        "business_type" as keyof SerializedUserProfile,
      type: "select" as const,
      options: [
        { value: 1, label: "製造業" },
        { value: 2, label: "情報通信業" },
        { value: 3, label: "小売業" },
        { value: 4, label: "サービス業" },
        { value: 5, label: "建設業" },
        { value: 6, label: "金融業" },
        { value: 7, label: "その他" },
      ],
    },
    {
      data: displayProfile.postal_code,
      text: "郵便番号",
      field:
        "postal_code" as keyof SerializedUserProfile,
      type: "text" as const,
      placeholder: "123-4567",
    },
    {
      data: displayProfile.address,
      text: "住所",
      field:
        "address" as keyof SerializedUserProfile,
      type: "text" as const,
      placeholder: "東京都渋谷区...",
    },
    {
      data: displayProfile.phone_number,
      text: "電話番号",
      field:
        "phone_number" as keyof SerializedUserProfile,
      type: "text" as const,
      placeholder: "03-1234-5678",
    },
    {
      data: displayProfile.representative_name,
      text: "代表者名",
      field:
        "representative_name" as keyof SerializedUserProfile,
      type: "text" as const,
      placeholder: "山田太郎",
    },
    {
      data: displayProfile.listing_status,
      text: "上場区分",
      field:
        "listing_status" as keyof SerializedUserProfile,
      type: "select" as const,
      options: [
        { value: 1, label: "東証プライム" },
        { value: 2, label: "東証スタンダード" },
        { value: 3, label: "東証グロース" },
        { value: 4, label: "非上場" },
      ],
    },
    {
      data: displayProfile.capital,
      text: "資本金（円）",
      field:
        "capital" as keyof SerializedUserProfile,
      type: "text" as const,
      placeholder: "10000000",
    },
    {
      data: displayProfile.established_at,
      text: "設立日",
      field:
        "established_at" as keyof SerializedUserProfile,
      type: "date" as const,
      placeholder: "YYYY-MM-DD",
    },
  ];

  const displayCompData = compData.filter(
    (el) => {
      if (isEditMode) return true;
      return (
        el.data !== undefined &&
        el.data !== null &&
        el.data !== ""
      );
    },
  );

  const formatDisplayValue = (
    item: (typeof compData)[0],
  ) => {
    if (
      item.type === "select" &&
      item.options &&
      item.data
    ) {
      if (item.field === "business_type") {
        return getBusinessType(Number(item.data));
      }
      if (item.field === "listing_status") {
        return getListingStatus(
          Number(item.data),
        );
      }
      const option = item.options.find(
        (opt: any) => opt.value === item.data,
      );
      return option?.label || item.data;
    }
    if (
      item.field === "postal_code" &&
      item.data
    ) {
      return `〒${item.data}`;
    }
    return item.data;
  };

  return (
    <div
      className={clsx(
        "mt-40",

        "grid",
        "grid-cols-[auto_1fr_auto]",
        "items-start",
        "max-md:flex",
        "max-md:flex-col",
      )}
    >
      <div
        className={clsx(
          "mb-20 flex items-center justify-between",
        )}
      >
        <h3
          className={clsx(
            "text-40 font-bold",
            "w-340",
            "max-md:text-24",
          )}
        >
          ABOUT US
        </h3>
      </div>

      {displayCompData.length === 0 &&
        !isEditMode && (
          <div
            className={clsx(
              "text-center",
              "py-40",
              "text-gray-400",
              "text-14",
              "max-md:w-full",
            )}
          >
            会社情報が登録されていません
          </div>
        )}

      {(displayCompData.length > 0 ||
        isEditMode) && (
        <div
          className={clsx(
            "relative",
            isEditMode
              ? "rounded-lg border bg-gray-50 p-20"
              : "",
          )}
        >
          <div
            className={clsx(
              isEditMode && "divide-y-[1px]",
            )}
          >
            {compData.map((item) => {
              if (
                !isEditMode &&
                (!item.data || item.data === "")
              )
                return null;

              return (
                <div
                  key={item.text}
                  className={clsx(
                    "grid",
                    "grid-cols-[1fr_2fr]",
                    "py-12",
                  )}
                >
                  <div
                    className={clsx(
                      "text-14",
                      "font-medium",
                    )}
                  >
                    {item.text}
                  </div>
                  <div
                    className={clsx("text-14")}
                  >
                    {isEditMode ? (
                      item.type === "select" ? (
                        <select
                          value={item.data || ""}
                          onChange={(e) =>
                            handleUpdateProfile(
                              item.field,
                              e.target.value
                                ? Number(
                                    e.target
                                      .value,
                                  )
                                : null,
                            )
                          }
                          className={clsx(
                            "w-full",
                            "p-8",
                            "border",
                            "rounded",
                            "text-14",
                            "focus:outline-none",
                            "focus:border-blue-500",
                          )}
                        >
                          <option value="">
                            選択してください
                          </option>
                          {item.options?.map(
                            (option) => (
                              <option
                                key={option.value}
                                value={
                                  option.value
                                }
                              >
                                {option.label}
                              </option>
                            ),
                          )}
                        </select>
                      ) : (
                        <input
                          type={item.type}
                          value={item.data || ""}
                          onChange={(e) =>
                            handleUpdateProfile(
                              item.field,
                              e.target.value,
                            )
                          }
                          placeholder={
                            item.placeholder
                          }
                          className={clsx(
                            "w-full",
                            "p-8",
                            "border",
                            "rounded",
                            "text-14",
                            "focus:outline-none",
                            "focus:border-blue-500",
                          )}
                        />
                      )
                    ) : (
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            formatDisplayValue(
                              item,
                            ) || "",
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isEditMode && (
        <div
          className={clsx(
            "flex",
            "gap-12",
            "justify-end",
            "w-260",
          )}
        >
          <button
            onClick={handleCancel}
            className={clsx(
              "px-20",
              "py-8",
              "border",
              "rounded-full",
              "text-12",
            )}
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            className={clsx(
              "px-20",
              "py-8",
              "bg-black",
              "text-white",
              "rounded-full",
              "text-12",
            )}
          >
            保存
          </button>
        </div>
      )}

      {isEditable && !isEditMode && (
        <div
          className={clsx("w-340", "text-right")}
        >
          <button
            onClick={() => setIsEditMode(true)}
            className={clsx(
              "text-12",
              "px-16",
              "py-6",
              "border",
              "rounded-full",
              "hover:bg-gray-50",
            )}
          >
            {displayCompData.length === 0
              ? "追加"
              : "編集"}
          </button>
        </div>
      )}
    </div>
  );
}
